/**
 * RAL Classic mit ungefähren Bildschirmwerten (sRGB), übernommen aus der
 * Farbtabelle im Wikipedia-Artikel „RAL-Farbe“ (Stand 24.09.2026).
 *
 * Die Hex-Werte sind Näherungen: RAL definiert Lackfarben, kein RGB. Effekt-
 * („Perl“, 9006, 9007) und Leuchtfarben lassen sich am Monitor gar nicht
 * wiedergeben. Wer daraus eine Unternehmensfarbe ableitet, bekommt deshalb
 * immer den Hinweis „ungefähr“ dazu (BrandColorField.tsx).
 */
export interface RalColor {
  code: string;
  name: string;
  hex: string;
}

export const RAL_CLASSIC: readonly RalColor[] = [
  { code: "1000", name: "Grünbeige", hex: "#cdba88" },
  { code: "1001", name: "Beige", hex: "#d0b084" },
  { code: "1002", name: "Sandgelb", hex: "#d2aa6d" },
  { code: "1003", name: "Signalgelb", hex: "#f9a800" },
  { code: "1004", name: "Goldgelb", hex: "#e2b007" },
  { code: "1005", name: "Honiggelb", hex: "#cb8e00" },
  { code: "1006", name: "Maisgelb", hex: "#e29000" },
  { code: "1007", name: "Narzissengelb", hex: "#e88c00" },
  { code: "1011", name: "Braunbeige", hex: "#af8a54" },
  { code: "1012", name: "Zitronengelb", hex: "#d9c022" },
  { code: "1013", name: "Perlweiß", hex: "#e3d9c6" },
  { code: "1014", name: "Elfenbein", hex: "#ddc49a" },
  { code: "1015", name: "Hellelfenbein", hex: "#e6d2b5" },
  { code: "1016", name: "Schwefelgelb", hex: "#eaf044" },
  { code: "1017", name: "Safrangelb", hex: "#f4b752" },
  { code: "1018", name: "Zinkgelb", hex: "#f3e03b" },
  { code: "1019", name: "Graubeige", hex: "#a4957d" },
  { code: "1020", name: "Olivgelb", hex: "#9a9464" },
  { code: "1021", name: "Rapsgelb", hex: "#f6b600" },
  { code: "1023", name: "Verkehrsgelb", hex: "#f7b500" },
  { code: "1024", name: "Ockergelb", hex: "#b89c50" },
  { code: "1026", name: "Leuchtgelb", hex: "#ffff00" },
  { code: "1027", name: "Currygelb", hex: "#a38c15" },
  { code: "1028", name: "Melonengelb", hex: "#ff9b00" },
  { code: "1032", name: "Ginstergelb", hex: "#e2a300" },
  { code: "1033", name: "Dahliengelb", hex: "#faab21" },
  { code: "1034", name: "Pastellgelb", hex: "#edab56" },
  { code: "1035", name: "Perlbeige", hex: "#a29985" },
  { code: "1036", name: "Perlgold", hex: "#927549" },
  { code: "1037", name: "Sonnengelb", hex: "#eea205" },
  { code: "1039", name: "Sandbeige", hex: "#cec19e" },
  { code: "1040", name: "Lehmbeige", hex: "#bbac81" },
  { code: "2000", name: "Gelborange", hex: "#da6e00" },
  { code: "2001", name: "Rotorange", hex: "#ba481b" },
  { code: "2002", name: "Blutorange", hex: "#c63927" },
  { code: "2003", name: "Pastellorange", hex: "#fa842b" },
  { code: "2004", name: "Reinorange", hex: "#e75b12" },
  { code: "2005", name: "Leuchtorange", hex: "#ff4912" },
  { code: "2007", name: "Leuchthellorange", hex: "#ffa421" },
  { code: "2008", name: "Hellrotorange", hex: "#ed6b21" },
  { code: "2009", name: "Verkehrsorange", hex: "#e15501" },
  { code: "2010", name: "Signalorange", hex: "#d4652f" },
  { code: "2011", name: "Tieforange", hex: "#e26e0e" },
  { code: "2012", name: "Lachsorange", hex: "#db6a50" },
  { code: "2013", name: "Perlorange", hex: "#954527" },
  { code: "2017", name: "RAL Orange", hex: "#fa4402" },
  { code: "3000", name: "Feuerrot", hex: "#a72920" },
  { code: "3001", name: "Signalrot", hex: "#9b2423" },
  { code: "3002", name: "Karminrot", hex: "#9b2321" },
  { code: "3003", name: "Rubinrot", hex: "#861a22" },
  { code: "3004", name: "Purpurrot", hex: "#6b1c23" },
  { code: "3005", name: "Weinrot", hex: "#59191f" },
  { code: "3007", name: "Schwarzrot", hex: "#3e2022" },
  { code: "3009", name: "Oxidrot", hex: "#6d332c" },
  { code: "3011", name: "Braunrot", hex: "#7e292c" },
  { code: "3012", name: "Beigerot", hex: "#cb8d73" },
  { code: "3013", name: "Tomatenrot", hex: "#9c322e" },
  { code: "3014", name: "Altrosa", hex: "#d47479" },
  { code: "3015", name: "Hellrosa", hex: "#d79fa6" },
  { code: "3016", name: "Korallenrot", hex: "#ac4034" },
  { code: "3017", name: "Rosé", hex: "#d3545f" },
  { code: "3018", name: "Erdbeerrot", hex: "#d14152" },
  { code: "3020", name: "Verkehrsrot", hex: "#bb1e10" },
  { code: "3022", name: "Lachsrot", hex: "#cc6855" },
  { code: "3024", name: "Leuchtrot", hex: "#ff2d21" },
  { code: "3026", name: "Leuchthellrot", hex: "#ff0000" },
  { code: "3027", name: "Himbeerrot", hex: "#b42041" },
  { code: "3028", name: "Reinrot", hex: "#cc2c24" },
  { code: "3031", name: "Orientrot", hex: "#a63437" },
  { code: "3032", name: "Perlrubinrot", hex: "#711521" },
  { code: "3033", name: "Perlrosa", hex: "#b24c43" },
  { code: "4001", name: "Rotlila", hex: "#8a5a83" },
  { code: "4002", name: "Rotviolett", hex: "#933d50" },
  { code: "4003", name: "Erikaviolett", hex: "#c45f8c" },
  { code: "4004", name: "Bordeauxviolett", hex: "#691639" },
  { code: "4005", name: "Blaulila", hex: "#83639d" },
  { code: "4006", name: "Verkehrspurpur", hex: "#992572" },
  { code: "4007", name: "Purpurviolett", hex: "#4a203b" },
  { code: "4008", name: "Signalviolett", hex: "#884d84" },
  { code: "4009", name: "Pastellviolett", hex: "#a38995" },
  { code: "4010", name: "Telemagenta", hex: "#c63678" },
  { code: "4011", name: "Perlviolett", hex: "#8773a1" },
  { code: "4012", name: "Perlbrombeer", hex: "#6b6880" },
  { code: "5000", name: "Violettblau", hex: "#384e6f" },
  { code: "5001", name: "Grünblau", hex: "#0f4c64" },
  { code: "5002", name: "Ultramarinblau", hex: "#00387b" },
  { code: "5003", name: "Saphirblau", hex: "#2a3756" },
  { code: "5004", name: "Schwarzblau", hex: "#191e28" },
  { code: "5005", name: "Signalblau", hex: "#005387" },
  { code: "5007", name: "Brillantblau", hex: "#41678d" },
  { code: "5008", name: "Graublau", hex: "#313c48" },
  { code: "5009", name: "Azurblau", hex: "#2e5978" },
  { code: "5010", name: "Enzianblau", hex: "#004f7c" },
  { code: "5011", name: "Stahlblau", hex: "#1a2b3c" },
  { code: "5012", name: "Lichtblau", hex: "#3481b8" },
  { code: "5013", name: "Kobaltblau", hex: "#193153" },
  { code: "5014", name: "Taubenblau", hex: "#6c7c98" },
  { code: "5015", name: "Himmelblau", hex: "#2874b2" },
  { code: "5017", name: "Verkehrsblau", hex: "#005a8c" },
  { code: "5018", name: "Türkisblau", hex: "#21888f" },
  { code: "5019", name: "Capriblau", hex: "#005e83" },
  { code: "5020", name: "Ozeanblau", hex: "#0b4151" },
  { code: "5021", name: "Wasserblau", hex: "#07737a" },
  { code: "5022", name: "Nachtblau", hex: "#222d5a" },
  { code: "5023", name: "Fernblau", hex: "#4d668e" },
  { code: "5024", name: "Pastellblau", hex: "#6a93b0" },
  { code: "5025", name: "Perlenzian", hex: "#296478" },
  { code: "5026", name: "Perlnachtblau", hex: "#102c54" },
  { code: "6000", name: "Patinagrün", hex: "#3c7460" },
  { code: "6001", name: "Smaragdgrün", hex: "#366735" },
  { code: "6002", name: "Laubgrün", hex: "#325928" },
  { code: "6003", name: "Olivgrün", hex: "#50533c" },
  { code: "6004", name: "Blaugrün", hex: "#024442" },
  { code: "6005", name: "Moosgrün", hex: "#114232" },
  { code: "6006", name: "Grauoliv", hex: "#3c392e" },
  { code: "6007", name: "Flaschengrün", hex: "#2c3222" },
  { code: "6008", name: "Braungrün", hex: "#37342a" },
  { code: "6009", name: "Tannengrün", hex: "#27352a" },
  { code: "6010", name: "Grasgrün", hex: "#4d6f39" },
  { code: "6011", name: "Resedagrün", hex: "#6c7c59" },
  { code: "6012", name: "Schwarzgrün", hex: "#303d3a" },
  { code: "6013", name: "Schilfgrün", hex: "#7d765a" },
  { code: "6014", name: "Gelboliv", hex: "#474135" },
  { code: "6015", name: "Schwarzoliv", hex: "#3d3d36" },
  { code: "6016", name: "Türkisgrün", hex: "#00694c" },
  { code: "6017", name: "Maigrün", hex: "#587f40" },
  { code: "6018", name: "Gelbgrün", hex: "#61993b" },
  { code: "6019", name: "Weißgrün", hex: "#b9ceac" },
  { code: "6020", name: "Chromoxidgrün", hex: "#37422f" },
  { code: "6021", name: "Blassgrün", hex: "#8a9977" },
  { code: "6022", name: "Braunoliv", hex: "#3a3327" },
  { code: "6024", name: "Verkehrsgrün", hex: "#008351" },
  { code: "6025", name: "Farngrün", hex: "#5e6e3b" },
  { code: "6026", name: "Opalgrün", hex: "#005f4e" },
  { code: "6027", name: "Lichtgrün", hex: "#7ebab5" },
  { code: "6028", name: "Kieferngrün", hex: "#315442" },
  { code: "6029", name: "Minzgrün", hex: "#006f3d" },
  { code: "6031", name: "Bronzegrün", hex: "#485746" },
  { code: "6032", name: "Signalgrün", hex: "#237f52" },
  { code: "6033", name: "Minttürkis", hex: "#46877f" },
  { code: "6034", name: "Pastelltürkis", hex: "#7aacac" },
  { code: "6035", name: "Perlgrün", hex: "#194d25" },
  { code: "6036", name: "Perlopalgrün", hex: "#04574b" },
  { code: "6037", name: "Reingrün", hex: "#008b29" },
  { code: "6038", name: "Leuchtgrün", hex: "#00b51a" },
  { code: "6039", name: "Fasergrün", hex: "#b3c53f" },
  { code: "6040", name: "Helloliv", hex: "#827e58" },
  { code: "7000", name: "Fehgrau", hex: "#7a888e" },
  { code: "7001", name: "Silbergrau", hex: "#8f999f" },
  { code: "7002", name: "Olivgrau", hex: "#817863" },
  { code: "7003", name: "Moosgrau", hex: "#7a7669" },
  { code: "7004", name: "Signalgrau", hex: "#9b9b9b" },
  { code: "7005", name: "Mausgrau", hex: "#6b716f" },
  { code: "7006", name: "Beigegrau", hex: "#756f61" },
  { code: "7008", name: "Khakigrau", hex: "#745e3d" },
  { code: "7009", name: "Grüngrau", hex: "#5d6058" },
  { code: "7010", name: "Zeltgrau", hex: "#585c56" },
  { code: "7011", name: "Eisengrau", hex: "#555d61" },
  { code: "7012", name: "Basaltgrau", hex: "#575d5e" },
  { code: "7013", name: "Braungrau", hex: "#575044" },
  { code: "7015", name: "Schiefergrau", hex: "#51565c" },
  { code: "7016", name: "Anthrazitgrau", hex: "#383e42" },
  { code: "7021", name: "Schwarzgrau", hex: "#2f3234" },
  { code: "7022", name: "Umbragrau", hex: "#4b4d46" },
  { code: "7023", name: "Betongrau", hex: "#818479" },
  { code: "7024", name: "Graphitgrau", hex: "#474a50" },
  { code: "7026", name: "Granitgrau", hex: "#374244" },
  { code: "7030", name: "Steingrau", hex: "#939388" },
  { code: "7031", name: "Blaugrau", hex: "#5b686d" },
  { code: "7032", name: "Kieselgrau", hex: "#b5b0a1" },
  { code: "7033", name: "Zementgrau", hex: "#818979" },
  { code: "7034", name: "Gelbgrau", hex: "#91886f" },
  { code: "7035", name: "Lichtgrau", hex: "#cbd0cc" },
  { code: "7036", name: "Platingrau", hex: "#9a9697" },
  { code: "7037", name: "Staubgrau", hex: "#7a7b7a" },
  { code: "7038", name: "Achatgrau", hex: "#b4b8b0" },
  { code: "7039", name: "Quarzgrau", hex: "#6b685e" },
  { code: "7040", name: "Fenstergrau", hex: "#9da3a6" },
  { code: "7042", name: "Verkehrsgrau A", hex: "#8f9695" },
  { code: "7043", name: "Verkehrsgrau B", hex: "#4e5451" },
  { code: "7044", name: "Seidengrau", hex: "#bdbdb2" },
  { code: "7045", name: "Telegrau 1", hex: "#8d9194" },
  { code: "7046", name: "Telegrau 2", hex: "#82898e" },
  { code: "7047", name: "Telegrau 4", hex: "#cfd0cf" },
  { code: "7048", name: "Perlmausgrau", hex: "#888175" },
  { code: "7050", name: "Tarngrau", hex: "#82887a" },
  { code: "8000", name: "Grünbraun", hex: "#89693e" },
  { code: "8001", name: "Ockerbraun", hex: "#99622d" },
  { code: "8002", name: "Signalbraun", hex: "#794d3e" },
  { code: "8003", name: "Lehmbraun", hex: "#7e4b26" },
  { code: "8004", name: "Kupferbraun", hex: "#8f4e35" },
  { code: "8007", name: "Rehbraun", hex: "#6f4a2f" },
  { code: "8008", name: "Olivbraun", hex: "#6e4a23" },
  { code: "8011", name: "Nussbraun", hex: "#5a3a29" },
  { code: "8012", name: "Rotbraun", hex: "#66332b" },
  { code: "8014", name: "Sepiabraun", hex: "#4a3526" },
  { code: "8015", name: "Kastanienbraun", hex: "#633a34" },
  { code: "8016", name: "Mahagonibraun", hex: "#492a1f" },
  { code: "8017", name: "Schokoladen&shy;braun", hex: "#442f29" },
  { code: "8019", name: "Graubraun", hex: "#3f3a3a" },
  { code: "8022", name: "Schwarzbraun", hex: "#211f20" },
  { code: "8023", name: "Orangebraun", hex: "#a65e2f" },
  { code: "8024", name: "Beigebraun", hex: "#765038" },
  { code: "8025", name: "Blassbraun", hex: "#755c49" },
  { code: "8027", name: "Lederbraun", hex: "#504938" },
  { code: "8028", name: "Terrabraun", hex: "#4e3b2b" },
  { code: "8029", name: "Perlkupfer", hex: "#773c27" },
  { code: "8031", name: "Sandbraun", hex: "#b49d7b" },
  { code: "9001", name: "Cremeweiß", hex: "#e9e0d2" },
  { code: "9002", name: "Grauweiß", hex: "#d7d5cb" },
  { code: "9003", name: "Signalweiß", hex: "#f4f8f4" },
  { code: "9004", name: "Signalschwarz", hex: "#2e3032" },
  { code: "9005", name: "Tiefschwarz", hex: "#0e0e10" },
  { code: "9006", name: "Weißaluminium", hex: "#a1a1a0" },
  { code: "9007", name: "Graualuminium", hex: "#878683" },
  { code: "9010", name: "Reinweiß", hex: "#f7f9ef" },
  { code: "9011", name: "Graphitschwarz", hex: "#292c2f" },
  { code: "9012", name: "Reinraumweiß", hex: "#fffde6" },
  { code: "9016", name: "Verkehrsweiß", hex: "#f7fbf5" },
  { code: "9017", name: "Verkehrsschwarz", hex: "#2a2d2f" },
  { code: "9018", name: "Papyrusweiß", hex: "#c7cac3" },
  { code: "9020", name: "Seidenmatt–Weiß", hex: "#fdfdfd" },
  { code: "9021", name: "Teerschwarz", hex: "#01050e" },
  { code: "9022", name: "Perlhellgrau", hex: "#9c9c9c" },
  { code: "9023", name: "Perldunkelgrau", hex: "#7e8182" }
];
